/**
 * The two status axes, as badge SPECS — the data half.
 *
 * Kept apart from the components that render them because a module exporting
 * both constants and components cannot hot-reload (react-refresh), and these
 * maps are read by more than one cell: the filter chips and the legend want the
 * same labels.
 *
 * A row says two independent things, and each is rendered where it belongs:
 *
 *  - `reviewStatus` — what a REVIEWER did. Always present; the Status column
 *    (`RowStatusCell`) is this axis and nothing else.
 *  - `mappingStatus` — what the PIPELINE concluded. Rendered by
 *    `RowFindingBadge`, under the suggestion it judges, because it is a claim
 *    about that cell's contents rather than about the row. Only shown when it
 *    is a FINDING; `mapped` means "nothing to report" and renders nothing, so a
 *    clean row carries no finding at all.
 *
 * These were icons. They are badges because an icon has to be decoded before it
 * can be read, and this table is scanned in bulk by someone deciding what to
 * accept — a word is faster than a glyph, and it survives being described out
 * loud in a review. Each badge keeps the long explanation as its tooltip, so
 * the short label never has to carry the whole meaning.
 *
 * Both maps are `Record<Union, …>` and therefore exhaustive: adding a member
 * to either enum fails the build here instead of rendering a blank cell.
 *
 * Every badge carries the FULL explanation as a tooltip and as `aria-label`.
 * The short text is what fits a table cell; it is never the whole meaning.
 *
 * `no_item_found` has NO badge, and that is a decision rather than a gap. The
 * finding is that the catalogue holds nothing for this part at all, so there is
 * no suggestion for a badge to annotate — wd-mapping renders "AI suggested"
 * over the model's proposed name instead, which says the same thing in the
 * place the reviewer is already reading. A badge here would have sat under an
 * empty cell. `no_value_found` DOES get one, because there the field matched
 * and a closest value is shown: the badge is a caveat on a value that exists.
 *
 * `auto_accepted` is a bullseye rather than a tick or an equals sign, and both
 * rejected alternatives are worth recording. A tick collides with `accepted` in
 * the column next door — the two would say "confirmed" twice for different
 * reasons. An equals sign is arithmetically honest (the match IS an identity,
 * not a similarity) but at 14px it is two thin rules with almost no silhouette,
 * and an icon nobody can pick out at a glance is not doing the one job it has.
 */

import {
  IconAlertTriangle,
  IconCheck,
  IconCircle,
  IconCircleX,
  IconExclamationCircle,
  IconPencil,
  IconSearchOff,
  IconTargetArrow,
} from "@tabler/icons-react";
import { MappingStatus, ReviewStatus } from "@models/job";

/**
 * The palette a status badge may use.
 *
 * A closed set, not a free string: a colour carries meaning here (red is "this
 * row is a problem"), so inventing one per call site is how two statuses end
 * up the same shade and a third ends up a colour that means nothing.
 */
export const StatusColor = {
  Neutral: "gray.5",
  Good: "teal.6",
  Bad: "red.6",
  Failure: "red.7",
  Edited: "blue.6",
  Warning: "orange.6",
  Machine: "yellow.7",
} as const;
export type StatusColor = (typeof StatusColor)[keyof typeof StatusColor];

export interface StatusBadgeSpec {
  /**
   * The glyph inside the badge, left of the text.
   *
   * An accelerator, never the carrier. These WERE the whole status, and that
   * was the problem: a picture of a status has to be decoded before it can be
   * read. Beside a word the roles invert — the word teaches the meaning once,
   * the shape finds it again on the five-hundredth row. It also makes the badge
   * legible without colour, which matters because colour is otherwise the only
   * thing separating a warning from a failure.
   */
  Icon: typeof IconCheck;
  color: StatusColor;
  /** i18n key for the tooltip + aria-label — the FULL explanation. */
  labelKey: string;
  /** Shown when the translation is missing. */
  fallback: string;
  /** i18n key for the badge's own text. */
  badgeKey: string;
  /**
   * The badge text, when the translation is missing.
   *
   * Deliberately short — two words at most. It sits in a table column beside
   * another badge, and a label that wraps is a label that stops being scannable.
   * Everything that does not fit lives in `fallback`, which is the tooltip.
   */
  badge: string;
}

/** What a REVIEWER did. Every row has one of these. */
export const REVIEW_STATUS_BADGE: Readonly<
  Record<ReviewStatus, StatusBadgeSpec>
> = {
  [ReviewStatus.Pending]: {
    Icon: IconCircle,
    color: StatusColor.Neutral,
    labelKey: "resultRow.status.pending",
    fallback: "Pending",
    badgeKey: "resultRow.statusBadge.pending",
    badge: "Pending",
  },
  [ReviewStatus.Accepted]: {
    Icon: IconCheck,
    color: StatusColor.Good,
    labelKey: "resultRow.status.accepted",
    fallback: "Accepted",
    badgeKey: "resultRow.statusBadge.accepted",
    badge: "Accepted",
  },
  [ReviewStatus.Rejected]: {
    Icon: IconCircleX,
    color: StatusColor.Bad,
    labelKey: "resultRow.status.rejected",
    fallback: "Rejected",
    badgeKey: "resultRow.statusBadge.rejected",
    badge: "Rejected",
  },
  [ReviewStatus.Corrected]: {
    Icon: IconPencil,
    color: StatusColor.Edited,
    labelKey: "resultRow.status.corrected",
    fallback: "Corrected",
    badgeKey: "resultRow.statusBadge.corrected",
    badge: "Corrected",
  },
  // Display-only: the client folds `mapping_status=failed` into this axis so
  // the Status cell reads one field. The API never sends it here.
  [ReviewStatus.Failed]: {
    Icon: IconExclamationCircle,
    color: StatusColor.Failure,
    labelKey: "resultRow.status.failed",
    fallback: "Failed",
    badgeKey: "resultRow.statusBadge.failed",
    badge: "Failed",
  },
};

/**
 * What the PIPELINE concluded. `null` means "no finding" — the row is fine and
 * the cell shows only the reviewer icon.
 *
 * `failed` is `null` here on purpose: it is already the reviewer icon (folded
 * in above), and two icons for one fact would read as two problems.
 */
export const MAPPING_STATUS_BADGE: Readonly<
  Record<MappingStatus, StatusBadgeSpec | null>
> = {
  [MappingStatus.Failed]: null,
  [MappingStatus.NoItemFound]: null,
  [MappingStatus.NoValueFound]: {
    Icon: IconSearchOff,
    color: StatusColor.Warning,
    labelKey: "jobResultsPage.status.noValueFound",
    fallback:
      "The field is right, but this exact value is not catalogued — the value shown is the closest one",
    badgeKey: "jobResultsPage.statusBadge.noValueFound",
    badge: "Not found",
  },
  [MappingStatus.NeedsReview]: {
    Icon: IconAlertTriangle,
    color: StatusColor.Warning,
    labelKey: "jobResultsPage.status.needsReview",
    fallback: "Low confidence — please double check",
    badgeKey: "jobResultsPage.statusBadge.needsReview",
    badge: "Require Review",
  },
  // "Exact Match", not a sparkle. This badge took the star's place because it is the
  // same fact stated plainly: for technical-manual a row is auto-accepted when
  // — and only when — the value catalogue matched the extracted value against
  // the field's COMPLETE value list. It is an arithmetic identification, not a
  // similarity, and it used to be advertised twice: once as a star here and
  // once as an "Exact" badge over in the value column.
  //
  // The old label ("from a confirmed example") described the golden-example
  // replay that fed this status before the catalogue existed. That path is
  // gone; wd-mapping still auto-accepts from a golden, which is why the
  // tooltip names both.
  [MappingStatus.AutoAccepted]: {
    Icon: IconTargetArrow,
    color: StatusColor.Good,
    labelKey: "jobResultsPage.status.autoAccepted",
    fallback:
      "Identified outright, not ranked — the catalogue holds this exact value for this field (or a confirmed past mapping was replayed). No reviewer had to confirm it.",
    badgeKey: "jobResultsPage.statusBadge.autoAccepted",
    badge: "Exact Match",
  },
  [MappingStatus.Mapped]: null,
};
